<script setup lang="ts">
import type AntiRobot from '@/components/base/form/AntiRobot.vue';
import type Documents from '@/components/base/form/Documents.vue';

type Props = {
  opacity?: boolean;
  allowGrow?: boolean;
};

const { opacity = false, allowGrow = false } = defineProps<Props>();
const emit = defineEmits(['check-pendency']);

const route = useRoute();
const { getFormattedBrazilianDate } = useDateTimeHelper();
const { $containment } = useNuxtApp();
const { t } = useTexts();
const { openToaster } = useLayoutStore();
const { isApproved: kycIsApproved } = useKyc();
const { updateUserAccount } = useUserDataStore();
const { captcha } = useRuntimeConfig().public;
const { turnstileAlwaysShow } = captcha;

const { isKycIntegrationEnabled } = useKyc();
const { isModuleNeededOnFirstWithdraw } = useModulesValidation();

const { updateUserDocumentCPF, updateUserDocumentCURP, getUserPixKey } =
  useUserDataStore();

const {
  userId,
  userCountry,
  userName,
  userDocument,
  userHasBirthdate,
  userBirthdate,
  userHasDocument,
  fetchUserProfile,
} = useUserData();

const formBirthDate = ref<string>('');

function handleSelectedDate(formattedDate: any) {
  if (typeof formattedDate !== 'string' || formattedDate === '') {
    formBirthDate.value = '';

    return;
  }

  formBirthDate.value = formattedDate;
}

async function onSaveBirthDateSubmit() {
  await $containment.handle('update_data', () => {
    sendData();
  });
}

async function sendData() {
  if (!userId.value || !antiRobotToken.value) {
    return;
  }

  const { $kyc } = useNuxtApp();
  const { temporaryPassword } = useModulesValidation();

  isLoading.value = true;

  const payload: any = {
    birth_date: formBirthDate.value,
    captcha_token: antiRobotToken.value,
  };

  if ($kyc?.current?.id) {
    payload.kyc_id = $kyc.current.id;
  }

  if (temporaryPassword.get()) {
    payload.password_check = temporaryPassword.get();
  }

  await updateUserAccount(userId.value, payload)
    .then(async () => {
      await fetchUserProfile({ force: true });
      openToaster({
        message: t('account.document_section.birthdate_update_success'),
        type: 'success',
      });
      isEditing.value = false;
    })
    .catch((err) => {
      const errorData = err.data;

      openToaster({
        message: errorData.message,
        type: 'error',
      });
    })
    .finally(() => {
      isLoading.value = false;
      emit('check-pendency');
    });
}

const formattedBirthDate = computed(() => {
  return userBirthdate.value && userBirthdate.value !== '0000-00-00'
    ? getFormattedBrazilianDate(userBirthdate.value)
    : '';
});

const antiRobotRef = ref<InstanceType<typeof AntiRobot> | null>(null);
const antiRobotToken = computed<string>(
  () => antiRobotRef.value?.finalCaptchaTokenValue ?? '',
);

function resetAntiRobotToken() {
  antiRobotRef.value?.$resetAllCaptcha();
}

const userCanAddDocument = computed<boolean>(() => {
  return userCountry.value === 'BRA' || userCountry.value === 'MEX';
});

const documentsRef = ref<InstanceType<typeof Documents> | null>(null);

const finalDocumentNumber = computed<string>(() => {
  return documentsRef.value?.documentNumberClean ?? '';
});

const finalDocumentName = computed<string>(() => {
  return documentsRef.value?.documentName ?? '';
});

const finalDocumentBirthDate = computed<string>(() => {
  return documentsRef.value?.documentBirthDate ?? '';
});

const isChangeDocumentBraVisible = computed<boolean>(() => {
  return (
    !!finalDocumentNumber.value &&
    !!finalDocumentName.value &&
    !!finalDocumentBirthDate.value
  );
});

const DEFAULT_CPF_MASK: string = '###.###.###-##';
const cpfMask = ref<string>(DEFAULT_CPF_MASK);

const maskByCountryCpf = computed<string[] | string>(() => {
  return userCountry.value === 'BRA' ? cpfMask.value : '';
});

function cpfMasked(cpf: string) {
  if (!cpf || cpf === '') return '';

  return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/gi, '$1.$2.$3-$4');
}

const isLoading = ref<boolean>(false);
const isEditing = ref<boolean>(false);

async function onSaveDocumentSubmit() {
  isLoading.value = true;

  const data = {
    number: finalDocumentNumber.value,
    captcha_token: antiRobotToken.value,
  };

  if (userCountry.value === 'MEX') {
    await updateUserDocumentCURP(data)
      .then(async () => {
        await fetchUserProfile({ force: true });
      })
      .catch((err) => {
        console.error('🚀 err:', err);
        openToaster({
          message: err.message,
          type: 'error',
        });
      })
      .finally(() => {
        isLoading.value = false;
      });
  } else {
    await updateUserDocumentCPF(data)
      .then(async () => {
        await fetchUserProfile({ force: true });
        await getUserPixKey();

        openToaster({
          message: t('account.document_section.document_update_success'),
          type: 'success',
        });
      })
      .catch((err) => {
        const message = err.data?.message
          ? err.data.message
          : t('account.document_section.update_error');

        openToaster({
          message,
          type: 'error',
        });
      })
      .finally(() => {
        isLoading.value = false;
        emit('check-pendency');
      });
  }
}

const hasPendency = computed(() => {
  return (
    !userHasDocument.value ||
    !userHasBirthdate.value ||
    (isKycIntegrationEnabled.value && !kycIsApproved.value)
  );
});

const badgeType = computed<'success' | 'error'>(() => {
  if (!userHasDocument.value) return 'error';
  if (!userHasBirthdate.value) return 'error';
  if (!isKycIntegrationEnabled.value) return 'success';
  if (!kycIsApproved.value) return 'error';

  return 'success';
});

const badgeLabel = computed<string>(() => {
  if (!userHasDocument.value) return t('account.document_section.no_document');
  if (!userHasBirthdate.value)
    return t('account.document_section.no_birthdate');
  if (!isKycIntegrationEnabled.value) return t('account.registered');
  if (!kycIsApproved.value) return t('account.document_section.kyc_pending');

  return t('account.verified');
});

const actionLabel = computed<string | undefined>(() => {
  if (isEditing.value) return t('general.close');
  if (!userHasDocument.value || !userHasBirthdate.value) return undefined;

  return t('general.see_data');
});

const actionType = computed<'view' | undefined>(() => {
  if (isEditing.value) return undefined;
  if (!userHasDocument.value || !userHasBirthdate.value) return undefined;

  return 'view';
});

const toggleEdit = () => {
  isEditing.value = !isEditing.value;
};

defineExpose({
  name: 'document',
  isEditing,
  hasPendency,
  toggleEdit,
});

onMounted(() => {
  if (route.query?.highlight === 'document' && hasPendency.value) {
    isEditing.value = true;
  }
});

watch(kycIsApproved, (oldValue, newValue) => {
  if (oldValue !== newValue && !!newValue) {
    isEditing.value = false;
    emit('check-pendency');
  }
});
</script>

<template>
  <ClientOnly>
    <BaseLoading
      class="!bg-auth-bg-primary !absolute"
      :icon-loader="true"
      :show="isLoading"
    />

    <div
      v-if="userCanAddDocument"
      :class="[
        $style.container,
        { [$style.opacity]: opacity, [$style.grow]: allowGrow && !isEditing },
      ]"
    >
      <UserProfileDataCard
        :action-label="actionLabel"
        :action-type="actionType"
        :badge-label="badgeLabel"
        :badge-type="badgeType"
        :label="$t('account.document_section.document')"
        :required="isModuleNeededOnFirstWithdraw('kyc')"
        @action="toggleEdit"
      >
        <template v-if="userHasDocument">
          {{ cpfMasked(userDocument) }}
          {{ userHasBirthdate ? ` - ${formattedBirthDate}` : '' }}
        </template>
        <span
          v-else
          :class="$style.registerBtn"
          @click="toggleEdit"
        >
          {{ $t('account.document_section.register_document') }}
        </span>
        <template
          v-if="
            !kycIsApproved &&
            userHasDocument &&
            isKycIntegrationEnabled &&
            !isEditing
          "
        >
          <BaseKycButton
            v-if="!$containment.isVerificationModalOpen"
            :content-class="$style.verifyBtn"
          >
            <span>{{ $t('account.document_section.verify_kyc') }}</span>
          </BaseKycButton>
        </template>
      </UserProfileDataCard>

      <div
        v-if="isEditing"
        :class="$style.editContent"
      >
        <div :class="$style.description">
          {{ $t('account.document_section.overview_personal_data') }}
        </div>

        <template v-if="userHasDocument">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-0 md:gap-4">
            <div :class="$style.formGroup">
              <label for="user_name">
                {{ $t('auth.register.cpf') }}
              </label>
              <BaseFormInput
                id="document"
                :key="userDocument"
                autocomplete="off"
                :data-maska="maskByCountryCpf"
                disabled
                :initial-value="userDocument"
                :model-value="userDocument"
                name="document"
                :placeholder="$t('auth.register.cpf')"
                type="text"
              />
            </div>

            <div
              v-if="userHasBirthdate"
              :class="$style.formGroup"
            >
              <label for="birth_date">
                {{ $t('account.birth_date') }}
              </label>

              <BaseFormInput
                id="birth_date"
                disabled
                :initial-value="formattedBirthDate"
                :model-value="formattedBirthDate"
                name="birth_date"
                :placeholder="$t('account.birth_date')"
                type="text"
              />
            </div>

            <div :class="$style.formGroup">
              <label for="user_name">
                {{ $t('account.user_data.name') }}
              </label>

              <BaseFormInput
                id="user_name"
                autocomplete="off"
                disabled
                :initial-value="userName"
                :model-value="userName"
                name="user_name"
                :placeholder="$t('account.user_data.name')"
                type="text"
              />
            </div>
          </div>

          <template v-if="!userHasBirthdate">
            <div :class="$style.formGroup">
              <label for="birth_date">
                {{ $t('account.birth_date') }}
              </label>
              <BaseFormSelectBirthdate
                id="birth_date"
                v-model="formBirthDate"
                :disabled="userHasBirthdate"
                :icon="false"
                :name="'birth_date'"
                :required="true"
                :separate-fields="true"
                @keydown.enter.prevent=""
                @update:selected-date="handleSelectedDate"
              />
            </div>

            <div
              :class="[
                $style.antiRobot,
                { [$style.isAbsolute]: !turnstileAlwaysShow },
              ]"
            >
              <BaseFormAntiRobot ref="antiRobotRef" />
            </div>
            <div class="flex justify-between items-end w-full col-span-2">
              <button
                v-if="!formBirthDate || !antiRobotToken"
                :class="$style.btnSubmit"
                disabled
              >
                <NuxtIcon
                  v-if="!antiRobotToken"
                  class="w-5 -mx-1 animate-spin"
                  name="loading"
                  style="width: 12px; height: 12px"
                />
                <NuxtIcon
                  v-else
                  name="check"
                />
                {{ $t('account.save') }}
              </button>
              <button
                v-else
                :class="$style.btnSubmit"
                :disabled="false"
                @click.prevent="onSaveBirthDateSubmit"
              >
                <NuxtIcon name="check" />
                {{ $t('account.save') }}
              </button>
            </div>
          </template>
        </template>

        <template v-else>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-0 md:gap-4">
            <div :class="$style.formGroup">
              <label for="document">
                {{ $t('account.user_data.document') }}
              </label>

              <BaseFormDocuments
                ref="documentsRef"
                :anti-robot-token="antiRobotToken"
                :hide-birthdate-field="true"
                :hide-name-field="true"
                @reset-anti-robot-token="resetAntiRobotToken"
              />
            </div>

            <div
              v-if="finalDocumentName"
              :class="$style.formGroup"
            >
              <label for="full_name">
                {{ $t('account.user_data.name') }}
              </label>

              <BaseFormInput
                id="full_name"
                autocomplete="off"
                disabled
                :initial-value="finalDocumentName"
                :model-value="finalDocumentName"
                name="full_name"
                :placeholder="$t('account.user_data.name')"
                type="text"
              />
            </div>

            <div
              v-if="finalDocumentBirthDate"
              :class="$style.formGroup"
            >
              <label for="birth_date">
                {{ $t('account.birth_date') }}
              </label>

              <BaseFormInput
                id="birth_date"
                autocomplete="off"
                disabled
                :initial-value="finalDocumentBirthDate"
                :model-value="finalDocumentBirthDate"
                name="birth_date"
                :placeholder="$t('account.birth_date')"
                type="text"
              />
            </div>
          </div>

          <div
            :class="[
              $style.antiRobot,
              { [$style.isAbsolute]: !turnstileAlwaysShow },
            ]"
          >
            <BaseFormAntiRobot ref="antiRobotRef" />
          </div>
          <div class="flex justify-between items-end w-full col-span-2">
            <button
              v-if="
                !isChangeDocumentBraVisible ||
                userHasDocument ||
                !antiRobotToken
              "
              :class="$style.btnSubmit"
              disabled
            >
              <NuxtIcon
                v-if="!antiRobotToken"
                class="w-5 -mx-1 animate-spin"
                name="loading"
                style="width: 12px; height: 12px"
              />
              <NuxtIcon
                v-else
                name="check"
              />
              {{ $t('account.save') }}
            </button>
            <button
              v-else
              :class="$style.btnSubmit"
              :disabled="false"
              @click.prevent="onSaveDocumentSubmit"
            >
              <NuxtIcon name="check" />
              {{ $t('account.save') }}
            </button>
          </div>
        </template>

        <div
          v-if="userHasDocument && isKycIntegrationEnabled"
          :class="$style.kycContainer"
        >
          <div
            v-if="kycIsApproved"
            :class="$style.checkIcon"
          >
            <NuxtIcon name="check" />
          </div>
          <NuxtIcon
            v-else
            :class="$style.faceIcon"
            name="face_id"
          />

          <p v-if="kycIsApproved">
            {{ $t('account.document_section.kyc_approved') }}
          </p>

          <p v-else>
            {{ $t('account.document_section.kyc_pending_description') }}
          </p>

          <BaseKycButton
            v-if="!kycIsApproved && !$containment.isVerificationModalOpen"
            :content-class="$style.btnValidate"
          >
            <span>{{ $t('account.document_section.verify_kyc_now') }}</span>
          </BaseKycButton>
        </div>
      </div>
    </div>
  </ClientOnly>
</template>

<style module lang="scss">
.container {
  @apply flex flex-col gap-3;
}

.grow {
  --card-grow: 1;
}

.opacity {
  @apply opacity-20 pointer-events-none select-none;
}

.registerBtn {
  @apply text-primary cursor-pointer;
}

.verifyBtn {
  @apply block text-xs text-primary mt-1 text-left;
}

.editContent {
  @apply w-full relative;

  .description {
    @apply text-sm md:text-sm text-texts/70 mb-4 max-w-[450px];
  }

  .formGroup {
    @apply z-10 mb-4 relative max-w-full md:max-w-80;

    label {
      @apply text-xs font-medium;
    }
  }

  .antiRobot {
    @apply opacity-60 whitespace-nowrap h-20 select-none z-0 sm:flex sm:justify-start sm:h-32 sm:-mt-6 sm:-mb-5;

    &.isAbsolute {
      @apply absolute -right-1 -bottom-0 w-40 h-px mb-8 md:-top-3;
    }
  }

  .btnSubmit {
    @apply bg-button-bg text-button-text py-2 px-4 rounded-md text-sm font-semibold whitespace-nowrap flex items-center justify-center gap-2;

    &[disabled]:not([disabled='false']) {
      @apply opacity-40 cursor-not-allowed;
    }
  }

  .kycContainer {
    @apply flex justify-between items-center flex-col lg:flex-row w-full border border-texts/40 border-dashed rounded-lg px-6 md:px-10 py-6 text-texts gap-4 text-center lg:text-left select-none mt-2;

    .checkIcon {
      @apply border-solid border-texts border-2 w-12 min-w-12 h-12 rounded-full flex items-center justify-center;
      svg {
        @apply text-2xl min-w-12 text-texts fill-texts;
      }
    }

    .faceIcon {
      @apply text-6xl min-w-12 text-texts fill-texts;
    }

    .btnValidate {
      @apply bg-button-bg text-button-text py-2 px-4 rounded-md text-sm font-semibold whitespace-nowrap flex items-center justify-center gap-2;
    }
  }
}
</style>
